'use client'

import React, { FC, PropsWithChildren, useLayoutEffect, useRef } from 'react'
import { flexRender, Table as TanTable, Column } from '@tanstack/react-table'
import { useInfiniteScroll } from '@dc-shared-web/hooks/utils/useInfiniteScroll'
import Spinner from '@dc-shared-web/components/base/Spinner'
import {
  Table as ShadcnTable,
  TableBody as ShadcnTableBody,
  TableCell as ShadcnTableCell,
  TableHead as ShadcnTableHead,
  TableHeader as ShadcnTableHeader,
  TableRow as ShadcnTableRow,
} from '@dc-shared-web/components/shadcn/table'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { Icon, IconColor } from '@dc-shared-web/components/base/Icon'
import { RowData } from '@tanstack/table-core'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  faArrowDownWideShort,
  faArrowUpWideShort,
  faSort,
} from '@document-crunch/pro-regular-svg-icons'
import { cn } from '@dc-shared-web/lib'

/* 
  https://tanstack.com/table/v8/docs/api/core/table#meta

  Unfortunately we're disabling some ESLint rules because the following code causes a lint error. We need the definitions to be exactly the same as the original ones otherwise we have a typescript error.
*/
declare module '@tanstack/table-core' {
  /* TODO: Remove this interface since we're using it to configure styles we already have for the table by default. */
  // eslint-disable-next-line no-unused-vars
  interface TableMeta<TData extends RowData> {
    row?: {
      className?: string
    }
  }

  // eslint-disable-next-line no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    widthClassName?: string
    /* TODO: Remove this prop since we just use it to set up the width and we're using the widthClassName prop instead. */
    className?: string
    /* TODO: Remove this props since we don't want to receive any custom style. */
    style?: {
      [key: string]: string
    }
  }
}

const tableVariants = cva(
  [
    /* Table wrapper takes the full width and height of the parent container. It's really important to remember checking if the container wrapping this component has those values properly configured so you don't end up with a table with height 0. */
    'tw-w-full tw-h-full',
    /* So the loading state (absolutely positioned) can be in the middle of the table. */
    'tw-relative',
    'tw-bg-white tw-rounded-md',
  ],
  {
    variants: {
      variant: {
        /* default styles involve having a border but no shadow. */
        default: 'tw-border',
        /* useful when we look for depth, which fits the use of shadow + border. */
        elevated: 'tw-border tw-shadow-lg',
        /* minimalistic style: no border and no shadow. */
        plain: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const tableHeaderTextClassNames =
  'tw-text-base tw-font-semibold tw-text-foreground'

const tableHeadVariants = cva(
  [tableHeaderTextClassNames, 'tw-flex tw-items-center'],
  {
    variants: {
      variant: {
        default: '',
        sortable: 'hover:tw-bg-primary-100',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface TableProps<TData> extends VariantProps<typeof tableVariants> {
  /**
   * Cells vertical alignment.
   * @default 'top'
   * */
  cellsAlignment?: 'top' | 'center'
  /** Whether the table is in a loading state. */
  isLoading?: boolean
  /**
   * Custom component to show when there are no results.
   * @default No results.
   * */
  noData?: React.ReactNode
  /** Callback fired when user scrolls near the bottom of the table. */
  onScrollEndReached?: () => void
  /** The table instance from @tanstack/react-table. */
  table: TanTable<TData>
  /** Callback fired when a row is clicked. */
  onRowClick?: (row: TData) => void
}

export function Table<TData>({
  cellsAlignment = 'top',
  isLoading,
  noData,
  onScrollEndReached = () => {},
  table,
  variant,
  onRowClick,
}: TableProps<TData>) {
  const bodyRef = useRef<HTMLTableSectionElement>(null)
  const headerRef = useRef<HTMLTableSectionElement>(null)

  const onScroll = useInfiniteScroll(50, onScrollEndReached)

  /* We need to calculate the scrollbar offset to properly align the header with the body. */
  const [scrollbarOffset, setScrollbarOffset] = React.useState(0)

  useLayoutEffect(() => {
    if (!bodyRef.current) return

    const scrollbarWidth =
      bodyRef.current.offsetWidth - bodyRef.current.clientWidth

    setScrollbarOffset(scrollbarWidth)

    // Create ResizeObserver to detect changes in the body element's dimensions
    const observer = new ResizeObserver(() => {
      if (!bodyRef.current) return

      const newScrollbarWidth =
        bodyRef.current.offsetWidth - bodyRef.current.clientWidth

      setScrollbarOffset(newScrollbarWidth)
    })

    observer.observe(bodyRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  const hasRows = table.getRowModel().rows?.length || isLoading

  /*
    If the column has a custom widthClassName, we return it. Otherwise, we default to 'flex-1' to ensure the column takes as much space as possible. You'll want columns to behave like this most of the time.
  */
  const getColumnWidth = (column: Column<TData>) => {
    return column.columnDef.meta?.widthClassName || 'tw-flex-1'
  }

  return (
    <div className={cn(tableVariants({ variant }))}>
      {isLoading && (
        <div className='tw-absolute tw-z-50 tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full'>
          <Spinner />
        </div>
      )}
      {/* When loading, we have a 50% opacity in the table content so the loading state is in front of it and provides more visual feedback. */}
      <ShadcnTable className={cn(isLoading && 'tw-opacity-50')}>
        <ShadcnTableHeader
          ref={headerRef}
          className={cn(
            'tw-flex tw-flex-col',
            /* Header columns have a fixed height and content is properly aligned vertically inside of it. */
            'tw-h-12'
          )}
          style={{ paddingRight: scrollbarOffset }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <ShadcnTableRow key={headerGroup.id} className='tw-flex'>
              {headerGroup.headers.map((header) => {
                /* If the column is NOT sortable (aka: it uses `enableSorting: false`) we render the column as a simple text. */
                if (!header.column.getCanSort()) {
                  return (
                    <ShadcnTableHead
                      key={header.id}
                      className={cn(
                        tableHeadVariants(),
                        getColumnWidth(header.column)
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </ShadcnTableHead>
                  )
                }

                /* This chunk of code means that the column is sortable. We render the column as a button with an icon to trigger the sorting. */

                const isSorted = header.column.getIsSorted()
                const sortIcon =
                  isSorted === false
                    ? faSort
                    : isSorted === 'asc'
                      ? faArrowUpWideShort
                      : faArrowDownWideShort

                return (
                  <ShadcnTableHead
                    key={header.id}
                    className={cn(
                      tableHeadVariants({
                        variant: 'sortable',
                      }),
                      getColumnWidth(header.column)
                    )}
                  >
                    <Button
                      /* This is a really custom button, so we use the primitive variant. */
                      variant='primitive'
                      /*
                        Custom styles:
                        - Button takes the full width of the parent TableHeader wrapper.
                        - We align content to the left.
                        - We remove x-axis padding that the button has by default.
                        - We configure some font styles.
                        - We add a gap between the button text and the sort icon.
                      */
                      className={cn(
                        'tw-w-full tw-px-0 tw-justify-start tw-gap-2',
                        tableHeaderTextClassNames
                      )}
                      onClick={() => header.column.toggleSorting()}
                      data-testid={`sort-button-${header.column.columnDef.header?.toString().toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      <Icon icon={sortIcon} color={IconColor.tertiary} />
                    </Button>
                  </ShadcnTableHead>
                )
              })}
            </ShadcnTableRow>
          ))}
        </ShadcnTableHeader>
        <ShadcnTableBody
          className='tw-flex tw-flex-col tw-overflow-auto tw-h-full'
          onScroll={onScroll}
          data-testid='data-table-body'
          ref={bodyRef}
        >
          {hasRows ? (
            table.getRowModel().rows.map((row) => (
              <ShadcnTableRow
                key={row.id}
                data-testid={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (row.original as any)?.uuid || (row.original as any)?.id
                }
                /* This is used to internally the component sets an specific css class. */
                data-state={row.getIsSelected() && 'selected'}
                className={cn(
                  'tw-flex hover:tw-bg-primary-100',
                  !variant || variant === 'default'
                    ? 'tw-border-b'
                    : 'tw-border-none',
                  !!onRowClick && 'tw-cursor-pointer',
                  cellsAlignment === 'center' && 'tw-items-center'
                )}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <ShadcnTableCell
                    key={cell.id}
                    className={cn(
                      'tw-text-base tw-break-all',
                      getColumnWidth(cell.column)
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </ShadcnTableCell>
                ))}
              </ShadcnTableRow>
            ))
          ) : (
            <EmptyState colSpan={table.getAllColumns().length}>
              {noData || <p className='tw-text-neutral-700'>No results.</p>}
            </EmptyState>
          )}
        </ShadcnTableBody>
      </ShadcnTable>
    </div>
  )
}

type EmptyStateProps = {
  colSpan: number
}

/* Empty state consist of a row with a cell that takes the full width and height of the table to render children both vertically and horizontally centered. */
const EmptyState: FC<PropsWithChildren<EmptyStateProps>> = ({
  colSpan,
  children,
}) => {
  return (
    <ShadcnTableRow className='tw-h-full'>
      <ShadcnTableCell
        colSpan={colSpan}
        className='tw-h-full tw-flex tw-items-center tw-justify-center'
      >
        {children}
      </ShadcnTableCell>
    </ShadcnTableRow>
  )
}
